import { useState } from 'react';
import { 
  BookmarkPlus, 
  BellDot, 
  EyeIcon
} from 'lucide-react';
import { formatActionTypeLabel, formatEmailDateDetailed, formatEmailText } from '../../utils/formatter';
import { getActionKey } from '../../utils/emailActionKeyHelper';
import { canDisplayMailAction, canWriteMail } from '../../utils/mailProvider';
import AddToProjectModal from '../Project/AddToProjectModal';
import EmailLabelSelectOptions from '../Shared/EmailLabelSelectOptions';
import GeoffreySummaryCard from './GeoffreySummaryCard';


// Custom Folders & Smart Email Routing (D-08): a reserved, non-canonical
// select value. Selecting it opens the shared CustomFolderModal instead of
// moving the email -- it must never collide with a real destination key
// (all real keys are either the 3 fixed system keys or `custom-<uuid>`).
export const ADD_FOLDER_SENTINEL = '__add_folder__';

const EmailCard = ({
  email,
  currentDigest,
  emailIndex,
  onCreateTask,
  onMoveEmail,
  onAddFolder,
  onAddEmailToProject,
  onViewTask,
  isProjectModalOpen,
  setIsProjectModalOpen,
  getActiveTask,
  getActiveProject,
  onViewProject,
  threadLength
}) => {
  const [isNotificationVisible] = useState(false);
  // Selecting Add Folder… must never leave the sentinel as the visible
  // selection (D-08: it never calls handleMoveEmail and the select resets).
  // The select is otherwise uncontrolled, so remounting it via `key` is the
  // simplest reliable reset for a native <select>.
  const [selectResetKey, setSelectResetKey] = useState(0);

  const handleDestinationSelect = (value) => {
    if (value === ADD_FOLDER_SENTINEL) {
      setSelectResetKey((key) => key + 1);
      onAddFolder?.(email);
      return;
    }
    onMoveEmail(email, value);
  };

  const triggerAddEmailToProject = async (projectId, projectTitle) => {
    onAddEmailToProject(email, projectId, projectTitle);
  }

  const hasAiSummary = Boolean(email.aiSummary);
  const previewFontSize = emailIndex === 0 ? '0.96rem' : '0.88rem';
  const emailPreview = hasAiSummary
    ? null
    : (email.actions?.length > 0
      ? email.actions.map((action) => action?.summary).join('\n\n')
      : email.snippet);
  const mailWriteContext = { mailbox: currentDigest?.mailbox, email };
  const visibleActions = email.actions?.filter((action) => canDisplayMailAction(mailWriteContext, action));
  const canMoveEmail = canWriteMail(mailWriteContext, 'move_email');

  return (
    <div>
      <div className={`relative my-1 bg-white ${emailIndex === 0 && threadLength > 1 && 'border-b border-slate-200 pb-4 mb-4'}`}>
        <div className={`${emailIndex !== 0 ? 'border-l border-b border-slate-200 pl-4 pt-2' : ''}`}>
          <div>
            {emailIndex !== 0 && (
              <div className="flex flex-row justify-between items-center gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1 mb-1">
                  {email.isUnread && <BellDot className="text-blue-500" size={18} />}
                  <h3 className={`text-sm ${emailIndex === 0 ? 'font-semibold' : ''}`}>{email.from}</h3>
                </div>
                <div className="text-sm font-normal text-gray-600 shrink-0">
                  {formatEmailDateDetailed(email?.date)}
                </div>
              </div>
            )}
          </div>
          <div className="relative mb-2">
            {hasAiSummary ? (
              <GeoffreySummaryCard
                summary={email.aiSummary}
                heldForReview={email.heldForReview}
                fontSize={previewFontSize}
              />
            ) : emailPreview ? (  
              <p className="text-slate-600 mt-1" style={{ fontSize: previewFontSize }}>
                {formatEmailText(emailPreview)}
              </p>
            ) : null}
          </div>

          {emailIndex !== 0 && visibleActions?.map((action, actionIndex) => (
            <div key={getActionKey(action, actionIndex)} className="flex flex-row items-center justify-start w-fit mb-2 border-stone-200 border gap-2 bg-white rounded-md p-1 mt-1">
              <div className="text-xs whitespace-nowrap text-white bg-stone-600 rounded-md px-2 py-1">
                {formatActionTypeLabel(action, currentDigest?.labels)}
              </div>
              <p className="text-xs text-gray-600 pr-2">
                {action.reason}
              </p>
            </div>
          ))}
        </div>

        {emailIndex === 0 && (
          <div className="container transition-all duration-500">
            <div className="mt-3">                          
              <div className="flex justify-between gap-2 mt-2 overflow-x-auto">
                <div className="flex gap-2">              
                  {getActiveTask(email) ? (
                    <>
                      <button onClick={() => onViewTask(email)}
                        data-amp-track-section="Digest Email List"
                        data-amp-track-action="View_Task_Click"
                        data-amp-track-description="View Task"
                        className="flex items-center gap-1 px-3 ml-0 py-1.5 text-sm rounded-md border border-solid border-slate-200 bg-transparent text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        <EyeIcon size={22} /> View Task
                      </button>
                    </>
                  ) : (
                    <button onClick={() => onCreateTask(email)}
                      data-amp-track-section="Digest Email List"
                      data-amp-track-action="Create_Task_Click"
                      data-amp-track-description="Create Task"
                      className="flex items-center gap-1 px-3 ml-0 py-1.5 text-sm rounded-md border border-solid border-slate-200 bg-transparent text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                      <BookmarkPlus size={22} /> Create a Task
                    </button>
                  )}
                </div>

                {canMoveEmail && <div className="flex gap-2">
                  <select
                    key={selectResetKey}
                    data-amp-track-section="Digest Email List"
                    data-amp-track-action="Move_Email_Click"
                    data-amp-track-description="Move Email"
                    onChange={(e) => handleDestinationSelect(e.target.value)}
                    defaultValue=""
                    className="rounded-md border border-solid border-slate-200 text-sm p-2 w-24 cursor-pointer"
                  >
                    <option value="" disabled>
                      {currentDigest?.destinations?.find(({ key }) => key === email.triageDestination)?.label || 'Move to...'}
                    </option>
                    <EmailLabelSelectOptions destinations={currentDigest?.destinations} />
                    {onAddFolder && <option value={ADD_FOLDER_SENTINEL}>Add Folder…</option>}
                  </select>
                </div>}
              </div>
            </div>
          </div>
        )}
      </div>

      
      {isProjectModalOpen && (
        <AddToProjectModal setIsProjectModalOpen={setIsProjectModalOpen} addItemToProject={triggerAddEmailToProject} />
      )}

      {isNotificationVisible && (
        <div className="fixed top-4 right-4 bg-green-500 text-lg text-bold text-white p-6 rounded-md shadow-lg z-50">
          Email added to project successfully!
        </div>
      )}
    </div>
  )
}

export default EmailCard;
